import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";

export interface DeviceDateStatusData {
  [deviceId: string]: { data: number; date: Date };
}

/**
 * Returns the user with the given id.
 * @param diagnosticIds of the diagnostics we want to fetch
 * @param deviceIds of the devices, the data is needed
 * @returns - Live Statusdata from the devices with the date where the status data was recorded
 */
export function useStatusDataByDeviceAndDate(
  devicesAndDates: { deviceId: string; dateTime: Date }[],
  diagnosticId: string,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();
  return useQuery({
    queryKey: ["statusData", activeSessionId, diagnosticId, devicesAndDates],

    queryFn: async ({ signal }) => {
      const res: DeviceDateStatusData = {};

      // Await the Promise.all to ensure all asynchronous operations are complete
      await Promise.all(
        devicesAndDates.map(async ({ deviceId, dateTime }) => {
          const data = await geotabClient.get(
            "StatusData",
            {
              fromDate: dateTime,
              toDate: dateTime,
              diagnosticSearch: { id: diagnosticId },
              deviceSearch: { id: deviceId },
            },
            1,
            signal,
          );
          if (data && data.length > 0) {
            res[deviceId] = {
              data: data[0]?.data,
              date: data[0]?.dateTime,
            };
          }
        }),
      );

      return res;
    },

    placeholderData: keepPreviousData,
  });
}
